import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Office, User } from '@shared/graphql/generated';
import { CallbackData } from '@shared/modals/callback-modal/shared/callback-data';
import { CallbackModalStoreService } from '@shared/modals/callback-modal/shared/callback-modal-store.service';
import { ToastType } from '@shared/models/toast-type';
import { FormCallbackService } from '@shared/services/form-callback.service';
import { ToastStoreService } from '@shared/stores/toast-store.service';
import { UserStoreService } from '@shared/stores/user-store.service';
import { FormFieldComponent } from '@shared/ui-components/form-field/form-field.component';
import { InputDirective } from '@shared/ui-components/form-field/shared/input.directive';
import { ModalContainerComponent } from '@shared/ui-components/modal-container/modal-container.component';
import { SubmitButtonComponent } from '@shared/ui-components/submit-button/submit-button.component';
import { EvlandValidators } from '@shared/utils/validators';
import { IMaskDirective } from 'angular-imask';
// import { ReCaptchaV3Service } from 'ng-recaptcha';
import { finalize } from 'rxjs';

declare var gtag;

const GTAG_SHOW_EVENT_NAME = 'callback_modal';

@Component({
  selector: 'evland-callback-modal',
  templateUrl: './callback-modal.component.html',
  styleUrls: [ './callback-modal.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IMaskDirective,
    InputDirective,
    ModalContainerComponent,
    FormFieldComponent,
    SubmitButtonComponent,
  ],
})
export class CallbackModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private callbackModal = inject(CallbackModalStoreService);
  private fb = inject(UntypedFormBuilder);
  private formCallbackService = inject(FormCallbackService);
  // private recaptchaV3Service = inject(ReCaptchaV3Service);
  private toast = inject(ToastStoreService);
  private userStore = inject(UserStoreService);

  currentUser: Signal<User>;
  form: UntypedFormGroup;
  officeOptions = [
    { value: Office.Kyiv, label: $localize `Київ` },
    { value: Office.Vinnytsia, label: $localize `Вінниця` },
  ];
  data: WritableSignal<CallbackData> = signal(null);
  isSubmitting: WritableSignal<boolean> = signal(false);

  constructor() {
    this.initForm();
    this.initSignals();
    this.initEffects();
  }

  ngOnInit(): void {
    this.initData();
  }

  onClose(): void {
    this.activeModal.close();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.show({ type: ToastType.Error, text: $localize `Будь ласка, перевірте ще раз правильність заповнення форми` });

      return;
    }

    if (this.form.invalid || this.form.pristine || this.isSubmitting()) return;

    this.isSubmitting.set(true);

    const { name, phone_number, ...restInput } = this.form.value;
    const { type, subject, brand_id, series_id, model_id, offer_id, color, sub_color } = this.data();

    // this.recaptchaV3Service.execute(action)
    this.formCallbackService.submit({
      ...restInput,
      subject,
      name,
      type,
      phone_number,
      ...(brand_id && { brand_id }),
      ...(series_id && { series_id }),
      ...(model_id && { model_id }),
      ...(offer_id && { offer_id }),
      ...(color && { color }),
      ...(sub_color && { sub_color }),
    })
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe({
        next: () => {
          const values = [
            ...(offer_id ? [ `offer_${offer_id}` ] : []),
            ...(model_id ? [ `model_${model_id}` ] : []),
            ...(series_id ? [ `series_${series_id}` ] : []),
            ...(brand_id ? [ `brand_${brand_id}` ] : []),
          ];

          try {
            gtag('event', type, {
              'value': !!values.length ? values.join(',') : 'none',
            });
          } catch (e) {
            console.error('gtag not found');
          }

          this.toast.show({
            text: $localize`Заявку успішно надіслано. Наші менеджери незабаром зв'яжуться з вами`,
            type: ToastType.Success
          });
          this.resetForm();
          this.activeModal.close();
        },
        error: () => this.toast.show({
          text: $localize`Помилка! Перевірте ще раз форму або спробуйте пізніше`,
          type: ToastType.Error
        }),
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: [ '', Validators.required ],
      phone_number: [ '', [ Validators.required, EvlandValidators.phoneNumber ] ],
      preferred_office: [ '', Validators.required ],
    });
  }

  private initData(): void {
    const data = this.callbackModal.currentS();

    try {
      gtag('event', GTAG_SHOW_EVENT_NAME, {
        'value': data.type
      });
    } catch (e) {
      console.error('gtag not found');
    }

    this.data.set(data);
  }

  private initEffects(): void {
    effect(() => {
      this.currentUser();

      this.resetForm();
    });
  }

  private initSignals(): void {
    this.currentUser = this.userStore.currentS;
  }

  private resetForm(): void {
    const currentUser = this.currentUser();

    if (!currentUser) {
      this.form.reset();

      return;
    }

    const { first_name, last_name, middle_name, phone_number, preferred_office } = currentUser;
    const name = [ last_name, first_name, middle_name ].filter(Boolean).join(' ');

    this.form.reset({
      ...(name && { name }),
      ...(phone_number && { phone_number }),
      ...(preferred_office && { preferred_office }),
    });
  }
}
